import ArrowDownIcon from '@ingka/ssr-icon/paths/arrow-down';
import ArrowLeftIcon from '@ingka/ssr-icon/paths/arrow-left';
import ArrowRightIcon from '@ingka/ssr-icon/paths/arrow-right';
import ArrowUpIcon from '@ingka/ssr-icon/paths/arrow-up';
import { ComponentIds, Entity } from '@inter-ikea-core/core';
import { Movement } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { getProductEntities } from 'ducks/core';
import { getShowMoveButtonsMenu, setShowMoveButtonsMenu } from 'ducks/moveButtonsMenu';
import usePrevious from 'hooks/usePrevious';
import { useCallback, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Button from 'ui/Button/Button';
import { MoveEntity } from './SceneEntityTabButtons';

const Wrapper = styled.div`
  position: absolute;
  top: ${({ theme }) => `calc(-${theme.space.xxl} / 2)`};
  left: ${({ theme }) => `calc(-${theme.space.xxl} / 2)`};
  margin-left: -12px;
  margin-top: -12px;
`;
//This file will be removed if we decide to not use buttons when moving things with your keyboard, until then keep it

export default function MoveButtonsMenu() {
  const coreApi = useCore();
  const entityManager = coreApi.getServices().entityManager;
  const showMoveButtonsMenu = useSelector(getShowMoveButtonsMenu);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [moveMenu, setMoveMenu] = useState<any>([]);
  const prevShowMoveButtonsMenu = usePrevious(showMoveButtonsMenu);
  const dispatch = useDispatch();

  const productsInScene = useSelector(getProductEntities);
  const enableFeature = false;

  const disableVerticalButtons = !selectedEntity?.hasComponent(ComponentIds.FRAME_COMPONENT_ID);

  const entityWithPosition =
    selectedEntity && coreApi.entities.getWithPositionById(selectedEntity.id);

  const updateMoveButtons = useCallback(() => {
    if (showMoveButtonsMenu) {
      const entityWithPosition =
        selectedEntity && coreApi.entities.getWithPositionById(selectedEntity.id);

      setMoveMenu(entityWithPosition);
    }
  }, [showMoveButtonsMenu, selectedEntity, coreApi.entities]);

  useEffect(() => {
    updateMoveButtons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsInScene]);

  useEffect(() => {
    entityManager.subscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, (entity: Entity) =>
      setSelectedEntity(entity)
    );
    entityManager.subscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, () => {
      setSelectedEntity(null);
    });
    return () => {
      entityManager.unsubscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, setSelectedEntity);
      entityManager.unsubscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, setSelectedEntity);
    };
  }, [entityManager]);

  useEffect(() => {
    return coreApi.subscribe('update', updateMoveButtons);
  }, [coreApi, updateMoveButtons]);

  useEffect(() => {
    if (showMoveButtonsMenu !== prevShowMoveButtonsMenu) {
      updateMoveButtons();
    }
  }, [updateMoveButtons, showMoveButtonsMenu, prevShowMoveButtonsMenu, coreApi.entities]);

  const handleEscClicked = () => {
    dispatch(setShowMoveButtonsMenu(false));
  };

  useHotkeys('esc', handleEscClicked);

  const handleClickDown = () => {
    MoveEntity(coreApi, Movement.DOWN);
  };

  const handleClickUp = () => {
    MoveEntity(coreApi, Movement.UP);
  };

  const handleClickLeft = () => {
    MoveEntity(coreApi, Movement.RIGHT);
  };

  const handleClickRight = () => {
    MoveEntity(coreApi, Movement.LEFT);
  };

  if (!showMoveButtonsMenu) return null;

  const positionX = entityWithPosition && entityWithPosition.position.x;
  const positionY = entityWithPosition && entityWithPosition.position.y;

  return enableFeature &&
    moveMenu &&
    showMoveButtonsMenu &&
    entityWithPosition &&
    selectedEntity ? (
    <>
      {disableVerticalButtons && (
        <Wrapper
          style={{
            left: positionX + `px`,
            top: positionY + `px`,
            transform: 'translate(-8px, -10px) translate(0, -100%)'
          }}
        >
          <Button
            id="move-up-button"
            dataTestId={`move-product-up-${entityWithPosition.entityId}`}
            type="emphasised"
            onClick={() => handleClickUp()}
            iconOnly
            size={'small'}
            ssrIcon={ArrowUpIcon}
          />
        </Wrapper>
      )}
      <Wrapper
        style={{
          left: positionX + `px`,
          top: positionY + `px`,
          transform: 'translate(-8px, -10px) translate(170%, 0px)'
        }}
      >
        <Button
          id="move-right-button"
          dataTestId={`move-product-right-${entityWithPosition.entityId}`}
          type="emphasised"
          onClick={() => handleClickRight()}
          iconOnly
          size={'small'}
          ssrIcon={ArrowRightIcon}
        />
      </Wrapper>
      {disableVerticalButtons && (
        <Wrapper
          style={{
            left: positionX + `px`,
            top: positionY + `px`,
            transform: 'translate(-8px, -10px) translate(0, 100%)'
          }}
        >
          <Button
            dataTestId={`move-product-down-${entityWithPosition.entityId}`}
            type="emphasised"
            onClick={() => handleClickDown()}
            iconOnly
            size={'small'}
            ssrIcon={ArrowDownIcon}
          />
        </Wrapper>
      )}
      <Wrapper
        style={{
          left: positionX + `px`,
          top: positionY + `px`,
          transform: 'translate(-8px, -10px) translate(-170%, 0px)'
        }}
      >
        <Button
          dataTestId={`move-product-left-${entityWithPosition.entityId}`}
          type="emphasised"
          onClick={() => handleClickLeft()}
          iconOnly
          size={'small'}
          ssrIcon={ArrowLeftIcon}
        />
      </Wrapper>
    </>
  ) : (
    <></>
  );
}
